import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../core/models/base-state/base-state';
import { AccessLogActor } from '../../models/access-log-actor';
import { AccessLogEntry } from '../../models/access-log-entry';
import { AccessLogFilters, DEFAULT_ACCESS_LOG_FILTERS } from '../../models/access-log-filters';
import * as actions from '../actions/access-logs.actions';

export const accessLogsFeatureKey = 'access-logs';

/**
 * Rows per request. Larger than `DEFAULT_PAGE_SIZE` (10), which would fire three requests just to
 * fill a desktop viewport on first paint, and comfortably inside the server's enforced page-size
 * maximum — every row there costs several correlated subqueries, so this is not a dial to raise
 * casually.
 */
export const ACCESS_LOG_PAGE_SIZE = 25;

export interface AccessLogsState extends EntityState<AccessLogEntry> {
  baseState: baseState.BaseState;
  filters: AccessLogFilters;
  /**
   * The "Accessed by" options as the server last returned them for the current Date/Type/search: only
   * people with matching activity, the patient's own account included only when it has some. See
   * `selectActorOptions` for what the pick list renders.
   */
  actors: AccessLogActor[];
  /**
   * The person picked under "Accessed by", captured when picked, so the label still names them after a
   * filter change leaves them with no matching entries. `null` when "All" is selected.
   */
  selectedActor: AccessLogActor | null;
  /** True once any pick-list read has succeeded since the page was entered. */
  hasLoadedActors: boolean;
  /**
   * Failures from the actor pick-list read, kept apart from `baseState.error` so a failed pick list
   * cannot make the activity list render its error state (and vice versa). The rows never depend on
   * this read: "You" is decided by the signed-in user's id, not by the pick list (bug 58618).
   */
  actorsError: unknown;
}

/**
 * No `sortComparer`: the server sorts by `CreatedOnUtc` descending and pages in that order, so
 * insertion order is already correct. A client-side comparer would also fight the server whenever a
 * new event lands between two page fetches.
 */
export const accessLogsAdapter = createEntityAdapter<AccessLogEntry>({
  selectId: entry => entry.patientPortalAccessLogId,
});

export const initialState: AccessLogsState = accessLogsAdapter.getInitialState({
  baseState: {
    ...baseState.createBaseState(undefined),
    pageSize: ACCESS_LOG_PAGE_SIZE,
  },
  filters: DEFAULT_ACCESS_LOG_FILTERS,
  actors: [],
  selectedActor: null,
  hasLoadedActors: false,
  actorsError: null,
});

/**
 * Back to a first-page read: drop every loaded entry and reset paging.
 *
 * Shared by the initial load and by every filter change, because all filtering happens server-side —
 * a narrowed filter changes the whole result set, not just which loaded rows are visible, so keeping
 * the old entries would show stale rows above the newly fetched first page.
 */
function resetForReload(state: AccessLogsState): AccessLogsState {
  return {
    ...accessLogsAdapter.removeAll(state),
    baseState: {
      ...state.baseState,
      error: null,
      isLoading: true,
      pageNumber: 1,
      isAtEndOfData: false,
      totalRecordCount: 0,
    },
  };
}

/**
 * The option being selected, looked up among the rendered options — which can include a previously kept
 * selection the server no longer returns, so re-selecting it keeps its snapshot rather than losing it.
 */
function findActor(state: AccessLogsState, actorId: number): AccessLogActor | null {
  if (state.selectedActor?.patientPortalUserId === actorId) {
    return state.selectedActor;
  }

  return state.actors.find(actor => actor.patientPortalUserId === actorId) ?? null;
}

/**
 * A Date/Type/search change: a first-page read, plus a fresh pick-list read (`getAccessLogActors$`). An
 * earlier failure's notice is stale by then, and its "Try again" would only cancel the request already on
 * its way.
 */
function resetForFilterChange(state: AccessLogsState): AccessLogsState {
  return { ...resetForReload(state), actorsError: null };
}

export const accessLogsReducer = createReducer<AccessLogsState>(
  initialState,

  // Entering the page: a first-page read with the filters back at their defaults, and the actor pick
  // list emptied. Clearing `actors` matters on a patient switch — the previous patient's proxies must
  // not stay selectable in the "Accessed by" filter while the new patient's list loads.
  on(
    actions.initAccessLogs,
    (state): AccessLogsState => ({
      ...resetForReload(state),
      filters: DEFAULT_ACCESS_LOG_FILTERS,
      actors: [],
      selectedActor: null,
      hasLoadedActors: false,
      actorsError: null,
    })
  ),

  on(actions.getAccessLogs, resetForReload),

  // Date/Type/search changes re-request the pick list (`getAccessLogActors$`), so they also clear an
  // earlier pick-list failure: its notice is stale, and its "Try again" would cancel the request already
  // on its way.
  on(actions.setSearchTerm, (state, { searchTerm }): AccessLogsState => {
    const reset = resetForFilterChange(state);
    return { ...reset, filters: { ...reset.filters, searchTerm } };
  }),

  on(actions.setDateRange, (state, { dateRange }): AccessLogsState => {
    const reset = resetForFilterChange(state);
    return { ...reset, filters: { ...reset.filters, dateRange } };
  }),

  on(actions.setTypeFilter, (state, { typeFilter }): AccessLogsState => {
    const reset = resetForFilterChange(state);
    return { ...reset, filters: { ...reset.filters, type: typeFilter } };
  }),

  on(actions.setActorFilter, (state, { actorId }): AccessLogsState => {
    const reset = resetForReload(state);
    return {
      ...reset,
      filters: { ...reset.filters, actorId },
      selectedActor: actorId === null ? null : findActor(state, actorId),
    };
  }),

  // Resets the three dropdown filters and deliberately preserves `searchTerm`: the search field owns
  // its own clear affordance (the x inside it) and the Clear control is only shown for non-default
  // *filter* selections, so wiping the typed term from here would discard input the patient can still
  // see in the box.
  on(actions.clearFilters, (state): AccessLogsState => {
    const reset = resetForFilterChange(state);
    return {
      ...reset,
      filters: { ...DEFAULT_ACCESS_LOG_FILTERS, searchTerm: reset.filters.searchTerm },
      selectedActor: null,
    };
  }),

  on(actions.nextPage, (state): AccessLogsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: (state.baseState.pageNumber ?? 0) + 1,
      },
    };
  }),

  // Retry of a failed page: clear the error and show loading, keeping pageNumber and every loaded
  // entry. Deliberately no removeAll and no pageNumber change — that is what separates it from
  // getAccessLogs above and nextPage before it.
  on(
    actions.retryPage,
    (state): AccessLogsState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(
    actions.getAccessLogsSuccess,
    (state, { entries, pageNumber, isAtEndOfData, totalRecordCount }): AccessLogsState => {
      return {
        ...accessLogsAdapter.addMany(entries, state),
        baseState: {
          ...state.baseState,
          error: null,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(actions.getAccessLogsFailure, (state, { error }): AccessLogsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  on(actions.getAccessLogActors, (state): AccessLogsState => ({ ...state, actorsError: null })),

  on(
    actions.getAccessLogActorsSuccess,
    (state, { actors }): AccessLogsState => ({
      ...state,
      actors,
      // Refreshed when the server still returns them (their revocation state may have changed); kept as
      // captured when it does not — the filter is still applied, so the label must still name them.
      // Looked up by the applied filter, not the snapshot, so a missing snapshot is filled when it can be.
      selectedActor: actors.find(actor => actor.patientPortalUserId === state.filters.actorId) ?? state.selectedActor,
      hasLoadedActors: true,
      actorsError: null,
    })
  ),

  on(
    actions.getAccessLogActorsFailure,
    (state, { error }): AccessLogsState => ({
      ...state,
      // The previously loaded list is deliberately left in place: a failed refresh should not empty a
      // pick list the patient may already have open. It may now be for earlier filters, which is what
      // the notice says (see the template).
      actorsError: error,
    })
  )
);
